import { BanknoteArrowUp, FileText, Loader2, RefreshCcw, Send } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import type { ClaimExpenseItem } from "@/features/claims/components/expenses-list";
import { useCurrentUser } from "@/hooks/use-current-user";
import { formatEur } from "@/lib/currency";
import { cn } from "@/lib/utils";

import { type AvailableCounterparty, useAvailableCounterparties } from "../api/use-payment-orders";
import { useGeneratePaymentReceipt, useSendPaymentReceipt } from "../api/use-traveler-payments";
import { familyAction, passengersLabel } from "../lib/traveler-payments";
import { CreatePaymentOrderModal } from "./create-payment-order-modal";

function formatDate(date: string | Date | null | undefined): string {
  if (!date) {
    return "";
  }
  return new Intl.DateTimeFormat("fr-FR", { day: "numeric", month: "long", year: "numeric" }).format(
    new Date(date),
  );
}

interface TravelerPaymentsProps {
  claimId: string;
  claimNumber: number;
  claimExpenses: ClaimExpenseItem[];
}

/** Port of payments/TravelerPayments.vue — per-family receipt lifecycle + payment order creation. */
export function TravelerPayments({ claimId, claimNumber, claimExpenses }: TravelerPaymentsProps) {
  const { isUpclaimDirection } = useCurrentUser();
  const counterpartiesQuery = useAvailableCounterparties(claimNumber);
  const generateReceipt = useGeneratePaymentReceipt(claimId);
  const sendReceipt = useSendPaymentReceipt(claimId);

  const [orderModalOpen, setOrderModalOpen] = useState(false);
  const [preselectedCounterpartyId, setPreselectedCounterpartyId] = useState<string | null>(null);

  // Claim may not have any counterparties yet (no payment step reached) —
  // render an empty-state rather than surfacing an error.
  const counterparties = counterpartiesQuery.isError ? [] : (counterpartiesQuery.data ?? []);
  const loading = counterpartiesQuery.isFetching;

  const passengerCounterparties = counterparties.filter(
    (counterparty) => counterparty.type?.type === "PASSENGER" && counterparty.liveBreakdown,
  );

  function openCreateOrder(counterparty: AvailableCounterparty) {
    setPreselectedCounterpartyId(counterparty.id);
    setOrderModalOpen(true);
  }

  return (
    <section className="-mx-4 py-6">
      <CreatePaymentOrderModal
        open={orderModalOpen}
        onOpenChange={(open) => {
          setOrderModalOpen(open);
          if (!open) {
            setPreselectedCounterpartyId(null);
          }
        }}
        onCreated={() => void counterpartiesQuery.refetch()}
        preselectedClaimNumber={claimNumber}
        preselectedCounterpartyId={preselectedCounterpartyId}
      />

      <div className="mb-4 px-3">
        <div className="flex items-center justify-between">
          <h3 className="inline-flex items-center text-sm font-semibold text-gray-900">
            <BanknoteArrowUp className="mr-2 h-5 w-5 text-gray-400" />
            Paiements aux voyageurs
          </h3>
          <button
            type="button"
            className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700"
            disabled={loading}
            onClick={() => void counterpartiesQuery.refetch()}
          >
            <RefreshCcw className={cn("h-3.5 w-3.5", loading && "animate-spin")} />
            Rafraîchir
          </button>
        </div>
        <p className="mt-1 text-xs text-gray-500">
          Répartition par famille signataire. Le net est recalculé en direct à partir des demandes monétaires
          reçues et des frais remboursés par la compagnie.
        </p>
      </div>

      {loading && !counterparties.length ? (
        <div className="px-3 py-8 text-center text-sm text-gray-500">
          <Loader2 className="mx-auto mb-2 h-4 w-4 animate-spin" />
          Chargement…
        </div>
      ) : !passengerCounterparties.length ? (
        <div className="mx-3 rounded-md border border-dashed border-gray-200 bg-gray-50 px-4 py-6 text-center text-sm text-gray-500">
          Aucun bénéficiaire passager disponible pour ce dossier. Les ordres de paiement ne peuvent être créés
          qu'une fois les acquisitions familiales validées et le dossier soldé par la compagnie.
        </div>
      ) : (
        <div className="space-y-4 px-3">
          {passengerCounterparties.map((counterparty) => {
            const action = familyAction(counterparty, claimExpenses);
            const breakdown = counterparty.liveBreakdown;
            const busy = generateReceipt.isPending || sendReceipt.isPending;

            return (
              <div key={counterparty.id} className="rounded-lg border border-gray-200 bg-white shadow-sm">
                {/* Family header */}
                <div className="flex items-start justify-between border-b border-gray-100 px-4 py-3">
                  <div className="text-left">
                    <p className="text-sm font-semibold text-gray-900">{passengersLabel(counterparty)}</p>
                    <p className="text-xs text-gray-500">{breakdown?.passengerCount} passager(s) actif(s)</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-500">Net à payer</p>
                    <p className="text-lg font-semibold text-emerald-700">
                      {formatEur(breakdown?.netCents ?? 0)}
                    </p>
                  </div>
                </div>

                {/* Breakdown */}
                <div className="grid gap-4 border-b border-gray-100 px-4 py-3 text-left text-xs text-gray-700 sm:grid-cols-2">
                  <dl className="space-y-1">
                    {(breakdown?.demands ?? []).map((demand) => (
                      <div key={demand.monetaryDemandId} className="flex justify-between">
                        <dt className="truncate">
                          {demand.expenseName || demand.displayText || demand.type}
                        </dt>
                        <dd className="font-mono">{formatEur(demand.amountCents)}</dd>
                      </div>
                    ))}
                    {!breakdown?.demands.length ? (
                      <div className="text-gray-400 italic">Aucun montant exigible pour cette famille.</div>
                    ) : null}
                  </dl>
                  <dl className="space-y-1">
                    <div className="flex justify-between">
                      <dt>Brut</dt>
                      <dd className="font-mono">{formatEur(breakdown?.grossCents ?? 0)}</dd>
                    </div>
                    <div className="flex justify-between text-gray-500">
                      <dt>− Upclaim ({breakdown?.commissionRates.upclaimPercentage}%)</dt>
                      <dd className="font-mono">−{formatEur(breakdown?.upclaimCommissionCents ?? 0)}</dd>
                    </div>
                    <div className="flex justify-between text-gray-500">
                      <dt>− Agence ({breakdown?.commissionRates.travelAgencyPercentage}%)</dt>
                      <dd className="font-mono">−{formatEur(breakdown?.travelAgencyCommissionCents ?? 0)}</dd>
                    </div>
                    <div className="flex justify-between border-t border-gray-200 pt-1 font-semibold text-gray-900">
                      <dt>Net</dt>
                      <dd className="font-mono">{formatEur(breakdown?.netCents ?? 0)}</dd>
                    </div>
                  </dl>
                </div>

                {/* Actions — state-driven button */}
                <div className="flex items-center justify-between border-t border-gray-100 bg-gray-50 px-4 py-2">
                  <div className="text-left text-xs text-gray-500">
                    {action === "pending-expenses" ? (
                      <span className="text-amber-600">Certains frais sont en attente de validation.</span>
                    ) : action === "awaiting-signature" ? (
                      <span>
                        Quittance envoyée pour signature le{" "}
                        {formatDate(counterparty.familyClaimAcquisition?.stepUpdatedAt)}
                      </span>
                    ) : action === "signed" ? (
                      <span>Quittance signée.</span>
                    ) : action === "create-order" && !counterparty.bankAccount ? (
                      <span className="text-amber-600">Coordonnées bancaires manquantes.</span>
                    ) : null}
                  </div>
                  <div className="flex items-center gap-2">
                    {/* Step 1: Generate receipt (all expenses decided, no draft yet) */}
                    {action === "generate-receipt" ? (
                      <Button
                        size="sm"
                        disabled={busy || (breakdown?.netCents ?? 0) <= 0}
                        onClick={() =>
                          generateReceipt.mutate({
                            familyClaimAcquisitionId: counterparty.familyClaimAcquisitionId as string,
                          })
                        }
                      >
                        <FileText data-icon="inline-start" />
                        Générer la quittance
                      </Button>
                    ) : null}

                    {/* Step 2: Send email (draft exists, not signed yet) */}
                    {action === "send-email" ? (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={busy}
                          onClick={() =>
                            generateReceipt.mutate({
                              familyClaimAcquisitionId: counterparty.familyClaimAcquisitionId as string,
                              force: true,
                            })
                          }
                        >
                          <FileText data-icon="inline-start" />
                          Re-générer
                        </Button>
                        <Button
                          size="sm"
                          disabled={busy || (breakdown?.netCents ?? 0) <= 0}
                          onClick={() => sendReceipt.mutate(counterparty.familyClaimAcquisitionId as string)}
                        >
                          <Send data-icon="inline-start" />
                          Envoyer la quittance
                        </Button>
                      </>
                    ) : null}

                    {/* Sent, not signed yet → let an admin resend the receipt email */}
                    {action === "awaiting-signature" ? (
                      <Tooltip>
                        <TooltipTrigger
                          render={
                            <Button
                              variant="outline"
                              size="icon-sm"
                              aria-label="Renvoyer la quittance"
                              disabled={busy}
                              onClick={() =>
                                sendReceipt.mutate(counterparty.familyClaimAcquisitionId as string)
                              }
                            />
                          }
                        >
                          <Send />
                        </TooltipTrigger>
                        <TooltipContent>Renvoyer la quittance</TooltipContent>
                      </Tooltip>
                    ) : null}

                    {/* Signed receipt → create the payment order (Upclaim direction only) */}
                    {action === "create-order" && isUpclaimDirection ? (
                      <Button size="sm" onClick={() => openCreateOrder(counterparty)}>
                        <Send data-icon="inline-start" />
                        Créer l'ordre de paiement
                      </Button>
                    ) : null}

                    {/* Disabled placeholder when expenses pending */}
                    {action === "pending-expenses" ? (
                      <Button size="sm" disabled>
                        <FileText data-icon="inline-start" />
                        Générer la quittance
                      </Button>
                    ) : null}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
